import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Wallet, Fuel, Route as RouteIcon,
  Users, MoreHorizontal, Save, Loader2, AlertTriangle, CheckCircle2,
  Calculator, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fcfa = (n) => `${fmt(n)} FCFA`;

const MC_ROWS = [
  { key: 'revenue', label: 'Revenu HT', goodWhenNegative: false },
  { key: 'cost', label: 'Coût à notre charge', goodWhenNegative: true },
  { key: 'margin', label: '= Marge', goodWhenNegative: false },
];

const BUDGET_FIELDS = [
  { poste: 'fuel', field: 'fuelBudgetMonthly', label: 'Carburant', icon: Fuel },
  { poste: 'tolls', field: 'tollsBudgetMonthly', label: 'Péages', icon: RouteIcon },
  { poste: 'driver', field: 'driverBudgetMonthly', label: 'Chauffeur', icon: Users },
  { poste: 'escort', field: 'escortBudgetMonthly', label: 'Escorte', icon: Users },
  { poste: 'handling', field: 'handlingBudgetMonthly', label: 'Manutention', icon: MoreHorizontal },
  { poste: 'cargo_insurance', field: 'cargoInsuranceBudgetMonthly', label: 'Assurance marchandise', icon: MoreHorizontal },
  { poste: 'fines', field: 'finesBudgetMonthly', label: 'Amendes', icon: AlertTriangle },
  { poste: 'route_fees', field: 'routeFeesBudgetMonthly', label: 'Frais de route', icon: RouteIcon },
  { poste: 'other', field: 'otherBudgetMonthly', label: 'Autres', icon: MoreHorizontal },
];

const KPICard = ({ icon: Icon, label, value, sub, tone = 'default', testId }) => {  const tones = {
    default: 'bg-gray-50 border-gray-100 text-gray-900',
    positive: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    negative: 'bg-red-50 border-red-200 text-red-900',
    info: 'bg-sky-50 border-sky-200 text-sky-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
  };
  return (
    <div className={`rounded-xl p-4 border ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <p className="text-xl font-bold tracking-tight font-mono">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const ContractMarginTab = ({ contractId, contract }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [margin, setMargin] = useState(null);
  const [ourPostes, setOurPostes] = useState(['fuel', 'tolls', 'driver', 'other']);
  const [form, setForm] = useState({
    monthlyFixedCost: '',
    costPerUnit: '',
    costPerTon: '',
    targetMonthlyVolume: '',
    totalMonthlyFleetCost: '',
    fleetCostItems: [],
    fuelBudgetMonthly: '',
    tollsBudgetMonthly: '',
    driverBudgetMonthly: '',
    escortBudgetMonthly: '',
    handlingBudgetMonthly: '',
    cargoInsuranceBudgetMonthly: '',
    finesBudgetMonthly: '',
    routeFeesBudgetMonthly: '',
    otherBudgetMonthly: '',
    otherBudgetLabel: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer cookie-auth' };
      const [csRes, marginRes, matrixRes] = await Promise.all([
        axios.get(`${API}/admin/contract-margin/${contractId}/cost-structure`, { headers }),
        axios.get(`${API}/admin/contract-margin/${contractId}`, { headers }),
        axios.get(`${API}/admin/contract-margin/${contractId}/charge-matrix`, { headers }),
      ]);
      setOurPostes(
        Object.entries(matrixRes.data?.matrix || {})
          .filter(([, v]) => v.bearer === 'baobab')
          .map(([k]) => k),
      );
      const cs = csRes.data?.costStructure || {};
      // Pre-fill fleetCostItems from contract.billingRules.fleetItems if empty
      let fleetCostItems = cs.fleetCostItems || [];
      const billingFleet = contract?.billingRules?.fleetItems || [];
      if (fleetCostItems.length === 0 && billingFleet.length > 0) {
        fleetCostItems = billingFleet.map(it => ({
          vehicleType: it.vehicleType,
          vehicleTypeLabel: it.vehicleTypeLabel || it.vehicleType,
          quantity: it.quantity || 0,
          monthlyCostPerTruck: 0,
        }));
      }
      setForm({
        monthlyFixedCost: cs.monthlyFixedCost ?? '',
        costPerUnit: cs.costPerUnit ?? '',
        costPerTon: cs.costPerTon ?? '',
        targetMonthlyVolume: cs.targetMonthlyVolume ?? '',
        totalMonthlyFleetCost: cs.totalMonthlyFleetCost ?? '',
        fleetCostItems,
        fuelBudgetMonthly: cs.fuelBudgetMonthly ?? '',
        tollsBudgetMonthly: cs.tollsBudgetMonthly ?? '',
        driverBudgetMonthly: cs.driverBudgetMonthly ?? '',
        escortBudgetMonthly: cs.escortBudgetMonthly ?? '',
        handlingBudgetMonthly: cs.handlingBudgetMonthly ?? '',
        cargoInsuranceBudgetMonthly: cs.cargoInsuranceBudgetMonthly ?? '',
        finesBudgetMonthly: cs.finesBudgetMonthly ?? '',
        routeFeesBudgetMonthly: cs.routeFeesBudgetMonthly ?? '',
        otherBudgetMonthly: cs.otherBudgetMonthly ?? '',
        otherBudgetLabel: cs.otherBudgetLabel ?? '',
        notes: cs.notes ?? '',
      });
      setMargin(marginRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement de la marge");
    } finally {
      setLoading(false);
    }
  }, [contractId, contract]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        monthlyFixedCost: form.monthlyFixedCost === '' ? null : Number(form.monthlyFixedCost),
        costPerUnit: form.costPerUnit === '' ? null : Number(form.costPerUnit),
        costPerTon: form.costPerTon === '' ? null : Number(form.costPerTon),
        targetMonthlyVolume: form.targetMonthlyVolume === '' ? null : Number(form.targetMonthlyVolume),
        fleetCostItems: (form.fleetCostItems || []).map(it => ({
          vehicleType: it.vehicleType,
          vehicleTypeLabel: it.vehicleTypeLabel,
          quantity: Number(it.quantity) || 0,
          monthlyCostPerTruck: Number(it.monthlyCostPerTruck) || 0,
        })),
        totalMonthlyFleetCost: null,
        fuelBudgetMonthly: form.fuelBudgetMonthly === '' ? null : Number(form.fuelBudgetMonthly),
        tollsBudgetMonthly: form.tollsBudgetMonthly === '' ? null : Number(form.tollsBudgetMonthly),
        driverBudgetMonthly: form.driverBudgetMonthly === '' ? null : Number(form.driverBudgetMonthly),
        escortBudgetMonthly: form.escortBudgetMonthly === '' ? null : Number(form.escortBudgetMonthly),
        handlingBudgetMonthly: form.handlingBudgetMonthly === '' ? null : Number(form.handlingBudgetMonthly),
        cargoInsuranceBudgetMonthly: form.cargoInsuranceBudgetMonthly === '' ? null : Number(form.cargoInsuranceBudgetMonthly),
        finesBudgetMonthly: form.finesBudgetMonthly === '' ? null : Number(form.finesBudgetMonthly),
        routeFeesBudgetMonthly: form.routeFeesBudgetMonthly === '' ? null : Number(form.routeFeesBudgetMonthly),
        otherBudgetMonthly: form.otherBudgetMonthly === '' ? null : Number(form.otherBudgetMonthly),
        otherBudgetLabel: form.otherBudgetLabel || null,
        notes: form.notes || null,
      };
      await axios.put(
        `${API}/admin/contract-margin/${contractId}/cost-structure`,
        payload,
        {}
      );
      toast.success("Structure de coût enregistrée");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const updateFleetCostItem = (idx, field, value) => {
    const items = [...form.fleetCostItems];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, fleetCostItems: items });
  };

  const billingMode = contract?.billingRules?.billingMode || 'per_unit';
  const isPerTon = billingMode === 'per_tonnage';
  const volumeMode = billingMode === 'per_unit' || isPerTon;
  const volumeUnit = isPerTon ? 'tonne' : 'transfert';
  const unitCost = Number(isPerTon ? form.costPerTon : form.costPerUnit) || 0;

  const monthlyBudget = (() => {
    let base = 0;
    if (Number(form.monthlyFixedCost) > 0) {
      base = Number(form.monthlyFixedCost);
    } else if ((form.fleetCostItems || []).length > 0) {
      base = (form.fleetCostItems || []).reduce(
        (s, it) => s + (Number(it.quantity) || 0) * (Number(it.monthlyCostPerTruck) || 0),
        0
      );
    }
    return base
      + BUDGET_FIELDS.filter(f => ourPostes.includes(f.poste))
        .reduce((s, f) => s + (Number(form[f.field]) || 0), 0);
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="contract-margin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const m = margin?.margin || {};
  const mg = margin?.marginGlobal || {};
  const mc = margin?.marginComparison || {};
  const rev = margin?.revenue || {};
  const cost = margin?.cost || {};
  const period = margin?.period || {};

  return (
    <div className="space-y-6" data-testid="contract-margin-tab">
      {/* Header alerts */}
      {!cost.hasCostStructure && (
        <div className="rounded-xl p-4 border border-amber-200 bg-amber-50 flex items-start gap-3"
             data-testid="margin-missing-cost-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Aucune structure de coût configurée</p>
            <p className="text-xs text-amber-800 mt-0.5">
              Sans structure de coût ni coût réel saisi, la marge de ce contrat est déclarée
              <b> non calculable</b> et exclue des consolidations (elle n'est plus présentée à 100 %).
            </p>
          </div>
        </div>
      )}

      {/* 🟢 MARGE GLOBALE — Formule simple : Facturé client − (Transporteur + Charges) */}
      <Card className="border-emerald-100" data-testid="margin-global-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              Marge globale du contrat
            </span>
            <Badge
              variant="outline"
              className={
                mg.reliability === 'high' ? 'border-emerald-300 text-emerald-700' :
                mg.reliability === 'medium' ? 'border-amber-300 text-amber-700' :
                'border-red-300 text-red-700'
              }
              data-testid="margin-global-reliability"
            >
              {mg.reliability === 'high' && 'Fiable — tous les coûts saisis'}
              {mg.reliability === 'medium' && `Partiel — ${mg.runsWithoutCost || 0}/${mg.totalRuns || 0} opérations sans coût`}
              {mg.reliability === 'low' && 'Peu fiable — aucun coût saisi'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[11px] text-gray-500 mb-3 font-mono">
            Formule : Facturé client (HT) − Coût transporteur − Charges (carburant + péages + chauffeur + autres)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Facturé client */}
            <div className="rounded-lg border border-sky-100 bg-sky-50 p-4" data-testid="margin-global-invoiced">
              <p className="text-[11px] font-medium text-sky-700 uppercase tracking-wider">Facturé au client</p>
              <p className="text-xl font-bold text-sky-900 font-mono mt-1">{fcfa(mg.invoicedToClient)}</p>
              <p className="text-[10px] text-sky-700 mt-1">HT · {rev.invoicesCount || 0} facture(s)</p>
            </div>

            {/* Coût transporteur */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="margin-global-carrier">
              <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wider">Coût transporteur</p>
              <p className="text-xl font-bold text-gray-900 font-mono mt-1">− {fcfa(mg.carrierCost)}</p>
              <p className="text-[10px] text-gray-500 mt-1">carrierCost sur runs</p>
            </div>

            {/* Charges */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="margin-global-charges">
              <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wider">Charges</p>
              <p className="text-xl font-bold text-gray-900 font-mono mt-1">− {fcfa(mg.charges)}</p>
              <div className="text-[10px] text-gray-500 mt-1 space-y-0.5">
                <div>Carburant : {fcfa(mg.chargesBreakdown?.fuel)}</div>
                <div>Péages : {fcfa(mg.chargesBreakdown?.tolls)}</div>
                <div>Chauffeur : {fcfa(mg.chargesBreakdown?.driver)}</div>
                {(mg.chargesBreakdown?.other || 0) > 0 && <div>Autres : {fcfa(mg.chargesBreakdown?.other)}</div>}
              </div>
            </div>

            {/* Marge */}
            <div
              className={`rounded-lg border p-4 ${
                (mg.margin || 0) >= 0 ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
              }`}
              data-testid="margin-global-result"
            >
              <p className={`text-[11px] font-medium uppercase tracking-wider ${
                (mg.margin || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'
              }`}>
                = Marge
              </p>
              <p className={`text-2xl font-bold font-mono mt-1 ${
                (mg.margin || 0) >= 0 ? 'text-emerald-900' : 'text-red-900'
              }`}>
                {mg.margin === null ? 'n/d' : fcfa(mg.margin)}
              </p>
              <p className={`text-xs font-semibold mt-1 ${
                (mg.margin || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'
              }`}>
                {mg.margin === null
                  ? (mg.statusReason || 'Non calculable')
                  : `${mg.marginPct ?? 0}% du facturé`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 📊 PRÉVU vs RÉEL — périmètres alignés (mois facturés / mois avec coût saisi) */}
      <Card data-testid="margin-comparison-prevu-reel-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex flex-wrap items-center gap-2">
            <Calculator className="w-4 h-4 text-sky-600" />
            Prévu vs Réel
            {!mc.hasExpected && (
              <Badge variant="outline" className="border-amber-300 text-amber-700 ml-auto text-[10px]"
                     data-testid="mc-no-expected">
                Comparaison partielle
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[11px] text-gray-500 mb-1">
            Le prévu et le réel sont comparés sur le <b>même périmètre</b> : le revenu sur les mois
            facturés, le coût et la marge sur les mois dont le coût prestataire est saisi.
          </p>
          <p className="text-[11px] text-gray-600 mb-3" data-testid="mc-basis-note">
            {mc.basis?.note}
            {mc.basis?.monthlyPrice ? ` Prix contractuel ${fcfa(mc.basis.monthlyPrice)}/mois.` : ''}
            {mc.basis?.monthlyBudgetOurCharge
              ? ` Budget à notre charge ${fcfa(mc.basis.monthlyBudgetOurCharge)}/mois.` : ''}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="mc-table">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-3 text-[11px] font-medium uppercase tracking-wider text-gray-500 w-1/4"></th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium uppercase tracking-wider text-sky-700">Prévu (contrat)</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium uppercase tracking-wider text-emerald-700">Réel (facturé/saisi)</th>
                  <th className="text-right py-2 pl-3 text-[11px] font-medium uppercase tracking-wider text-gray-700">Δ Delta</th>
                </tr>
              </thead>
              <tbody className="font-mono text-sm">
                {MC_ROWS.map(({ key, label, goodWhenNegative }) => {
                  const exp = mc.expected?.[key];
                  const act = mc.actual?.[key];
                  const delta = mc.delta?.[key];
                  const deltaPct = mc.delta?.[`${key}Pct`];
                  const favourable = delta === null || delta === undefined
                    ? null
                    : (goodWhenNegative ? delta <= 0 : delta >= 0);
                  const isMargin = key === 'margin';
                  return (
                    <tr key={key}
                        className={isMargin ? 'bg-gray-50 border-t-2 border-gray-300' : 'border-b border-gray-100'}
                        data-testid={`mc-row-${key}`}>
                      <td className={`py-2 pr-3 font-sans ${isMargin ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {label}
                      </td>
                      <td className={`py-2 px-3 text-right text-sky-900 ${isMargin ? 'font-bold' : ''}`}>
                        {exp === null || exp === undefined
                          ? <span className="text-gray-400 text-xs font-sans">n/d</span>
                          : fcfa(exp)}
                        {isMargin && mc.expected?.marginPct !== null && mc.expected?.marginPct !== undefined && (
                          <div className="text-[10px] font-sans font-normal text-sky-700">{mc.expected.marginPct}%</div>
                        )}
                      </td>
                      <td className={`py-2 px-3 text-right text-emerald-900 ${isMargin ? 'font-bold' : ''}`}>
                        {act === null || act === undefined
                          ? <span className="text-gray-400 text-xs font-sans">n/d</span>
                          : fcfa(act)}
                        {isMargin && mc.actual?.marginPct !== null && mc.actual?.marginPct !== undefined && (
                          <div className="text-[10px] font-sans font-normal text-emerald-700">{mc.actual.marginPct}%</div>
                        )}
                      </td>
                      <td className={`py-2 pl-3 text-right font-semibold ${
                        favourable === null ? 'text-gray-400'
                          : favourable ? 'text-emerald-700' : 'text-red-700'}`}>
                        {favourable === null ? (
                          <span className="text-xs font-sans">non comparable</span>
                        ) : (
                          <>
                            {delta >= 0 ? '+' : ''}{fcfa(delta)}
                            {deltaPct !== null && deltaPct !== undefined && (
                              <span className="ml-1 text-[10px] font-sans">
                                ({deltaPct >= 0 ? '+' : ''}{deltaPct}%)
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {mc.delta?.revenuePct !== null && mc.delta?.revenuePct !== undefined
            && Math.abs(mc.delta.revenuePct) > 25 && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3"
                 data-testid="mc-billing-deviation">
              <AlertTriangle className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
              <p className="text-xs text-sky-900">
                La facturation s'écarte de {mc.delta.revenuePct >= 0 ? '+' : ''}{mc.delta.revenuePct}%
                du prix mensuel de référence du contrat — ce n'est pas nécessairement une anomalie
                (avenant, camions supplémentaires, facturation au réel), mais le « prévu » ci-dessus
                n'est plus la bonne référence tant que le prix mensuel du contrat n'est pas mis à jour.
              </p>
            </div>
          )}

          {mc.reason && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
                 data-testid="mc-reason">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-900">{mc.reason}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Summary (détails techniques : budget vs réel) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard
          icon={Wallet}
          label="Revenu (HT)"
          value={fcfa(rev.total)}
          sub={rev.method === 'invoiced' ? `${rev.invoicesCount} facture(s)` : 'estimé'}
          tone="info"
          testId="margin-kpi-revenue"
        />
        <KPICard
          icon={Calculator}
          label="Coût budgété"
          value={fcfa(cost.budgetedTotal)}
          sub={`${fcfa(cost.budgetedMonthly)}/mois × ${period.monthsActive || 0} mois`}
          tone="default"
          testId="margin-kpi-cost-budgeted"
        />
        <KPICard
          icon={cost.actualTotal > 0 ? TrendingUp : RefreshCw}
          label="Coût réel capturé"
          value={fcfa(cost.actualTotal)}
          sub={`${cost.runsWithActual || 0}/${cost.totalRuns || 0} opérations`}
          tone={cost.actualTotal > 0 ? 'warning' : 'default'}
          testId="margin-kpi-cost-actual"
        />
        <KPICard
          icon={(m.actual || 0) > 0 ? TrendingUp : TrendingDown}
          label="Marge réelle"
          value={m.actualPct === null || m.actualPct === undefined ? 'n/d' : `${m.actualPct}%`}
          sub={m.actual === null || m.actual === undefined
            ? 'aucun coût à notre charge saisi'
            : fcfa(m.actual)}
          tone={m.actualPct === null || m.actualPct === undefined
            ? 'default'
            : (m.actualPct >= 20 ? 'positive' : m.actualPct >= 10 ? 'warning' : 'negative')}
          testId="margin-kpi-margin-actual"
        />
      </div>

      {/* Cost Structure Form */}
      <Card data-testid="margin-cost-structure-form">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Structure de coût budgétée</span>
            <Badge variant="outline" className="font-mono">
              {volumeMode
                ? (unitCost > 0 ? `${fcfa(unitCost)}/${volumeUnit}` : 'Coût unitaire non renseigné')
                : `${fcfa(monthlyBudget)}/mois`}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Option 1 : Coût unitaire (contrats à l'unité / au tonnage) */}
          {volumeMode ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="unit-cost" className="text-xs font-medium">
                  Coût par {volumeUnit} (ce que nous payons au prestataire)
                </Label>
                <Input
                  id="unit-cost"
                  type="number"
                  placeholder="0"
                  value={isPerTon ? form.costPerTon : form.costPerUnit}
                  onChange={e => setForm({
                    ...form,
                    [isPerTon ? 'costPerTon' : 'costPerUnit']: e.target.value,
                  })}
                  className="mt-1 font-mono"
                  data-testid="margin-input-unitCost"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Budget du mois = coût par {volumeUnit} × volume réalisé.
                </p>
              </div>
              <div>
                <Label htmlFor="target-volume" className="text-xs font-medium">
                  Volume mensuel cible ({isPerTon ? 'tonnes' : 'transferts'}) — optionnel
                </Label>
                <Input
                  id="target-volume"
                  type="number"
                  placeholder="Laisser vide = volume réellement réalisé"
                  value={form.targetMonthlyVolume}
                  onChange={e => setForm({ ...form, targetMonthlyVolume: e.target.value })}
                  className="mt-1 font-mono"
                  data-testid="margin-input-targetMonthlyVolume"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Sert de référence au « Prévu » du chiffre d'affaires.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <Label htmlFor="monthly-fixed-cost" className="text-xs font-medium">
                Coût mensuel forfaitaire (laisser vide pour utiliser les coûts par camion)
              </Label>
              <Input
                id="monthly-fixed-cost"
                type="number"
                placeholder="0"
                value={form.monthlyFixedCost}
                onChange={e => setForm({ ...form, monthlyFixedCost: e.target.value })}
                className="mt-1 font-mono"
                data-testid="margin-input-monthlyFixedCost"
              />
            </div>
          )}

          {/* Option 2 : Coût par camion (fleet) */}
          {(form.fleetCostItems || []).length > 0 && (
            <>
              <Separator />
              <div>
                <Label className="text-xs font-medium">Coût mensuel par camion (par type)</Label>
                <div className="mt-2 space-y-2">
                  {form.fleetCostItems.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`margin-fleet-row-${idx}`}>
                      <div className="col-span-5 text-sm text-gray-700">{it.vehicleTypeLabel}</div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={it.quantity}
                          onChange={e => updateFleetCostItem(idx, 'quantity', e.target.value)}
                          className="font-mono text-sm h-9"
                          data-testid={`margin-fleet-quantity-${idx}`}
                        />
                      </div>
                      <div className="col-span-5">
                        <Input
                          type="number"
                          placeholder="Coût mensuel par camion"
                          value={it.monthlyCostPerTruck}
                          onChange={e => updateFleetCostItem(idx, 'monthlyCostPerTruck', e.target.value)}
                          className="font-mono text-sm h-9"
                          data-testid={`margin-fleet-cost-${idx}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Additional monthly budgets — uniquement les postes à NOTRE charge */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {BUDGET_FIELDS.filter(f => ourPostes.includes(f.poste)).map(f => (
              <div key={f.poste}>
                <Label htmlFor={`budget-${f.poste}`} className="text-xs flex items-center gap-1">
                  <f.icon className="w-3 h-3" /> {f.label} /mois
                </Label>
                <Input
                  id={`budget-${f.poste}`}
                  type="number"
                  placeholder="0"
                  value={form[f.field]}
                  onChange={e => setForm({ ...form, [f.field]: e.target.value })}
                  className="mt-1 font-mono text-sm h-9"
                  data-testid={`margin-input-${f.field}`}
                />
              </div>
            ))}
          </div>
          {BUDGET_FIELDS.filter(f => !ourPostes.includes(f.poste)).length > 0 && (
            <p className="text-[11px] text-gray-500" data-testid="budget-excluded-note">
              Non demandé (pas à notre charge d'après la matrice du contrat) :{' '}
              {BUDGET_FIELDS.filter(f => !ourPostes.includes(f.poste)).map(f => f.label).join(', ')}.
            </p>
          )}

          <div>
            <Label htmlFor="notes" className="text-xs">Notes (optionnel)</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="mt-1 text-sm"
              placeholder="Ex: tarif négocié pour ce contrat, conditions spéciales..."
              data-testid="margin-input-notes"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="margin-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer la structure de coût
            </Button>
          </div>
        </CardContent>
      </Card>

      {cost.hasCostStructure && (
        <Card className="border-emerald-100 bg-emerald-50/30">
          <CardContent className="py-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="text-xs text-emerald-900">
              <strong>Structure de coût configurée.</strong> La marge de ce contrat est désormais consolidée
              dans les KPIs Cockpit Finance et Profitabilité globale.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContractMarginTab;
